import type { HttpClient } from '@shared/http/http-client';
import type { IEmployeePortalRepository } from '@domain/data-contracts/employee-portal-repository.interface';
import type {
  EmployeeNextStep,
  EmployeeTask,
  EmployeeTaskDetail,
} from '@domain/models/employee-task.interface';
import {
  mapEmployeeNextStepDto,
  mapEmployeeTaskDetailDto,
  mapEmployeeTaskDto,
  type EmployeeNextStepDto,
  type EmployeeTaskDetailDto,
  type EmployeeTaskDto,
} from './mapping.helper';

const BASE = '/api/employee/tasks';

export class EmployeePortalRepository implements IEmployeePortalRepository {
  public constructor(private readonly http: HttpClient) {}

  public async listMyTasks(): Promise<readonly EmployeeTask[]> {
    const response = await this.http.get<readonly EmployeeTaskDto[]>(`${BASE}/mine`);
    return response.data.map(mapEmployeeTaskDto);
  }

  public async getTaskById(id: string): Promise<EmployeeTaskDetail> {
    const response = await this.http.get<EmployeeTaskDetailDto>(
      `${BASE}/${encodeURIComponent(id)}`,
    );
    return mapEmployeeTaskDetailDto(response.data);
  }

  public async getTaskByBarcode(barcode: string): Promise<EmployeeTask | null> {
    const response = await this.http.get<EmployeeTaskDto | null>(
      `${BASE}/by-barcode/${encodeURIComponent(barcode)}`,
    );
    if (!response.data) {
      return null;
    }
    return mapEmployeeTaskDto(response.data);
  }

  public async completeTask(id: string): Promise<EmployeeTask> {
    const response = await this.http.post<EmployeeTaskDto>(
      `${BASE}/${encodeURIComponent(id)}/complete`,
    );
    return mapEmployeeTaskDto(response.data);
  }

  public async listNextSteps(id: string): Promise<readonly EmployeeNextStep[]> {
    const response = await this.http.get<readonly EmployeeNextStepDto[]>(
      `${BASE}/${encodeURIComponent(id)}/next-steps`,
    );
    return response.data.map(mapEmployeeNextStepDto);
  }

  public async handoffTask(id: string, nextStepId: string): Promise<EmployeeTask> {
    const response = await this.http.post<EmployeeTaskDto>(
      `${BASE}/${encodeURIComponent(id)}/handoff`,
      { nextStepId },
    );
    return mapEmployeeTaskDto(response.data);
  }
}
